import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { get, useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { PassosBento } from "../seguros/cotacao/PassosBento";

/**
 * Um cadastro longo dividido em passos, com Voltar e Avançar — a mesma
 * linguagem da jornada de cotação de seguros (`seguros/cotacao/`).
 *
 * A ficha de contato inteira numa tela só eram mais de trinta campos de uma
 * vez; ninguém sabia por onde começar nem o que era obrigatório. Aqui cada
 * passo faz UMA pergunta e mostra só os campos dela.
 *
 * Todos os passos ficam MONTADOS e os inativos só escondidos: é um formulário
 * react-hook-form só, e desmontar um passo levaria junto os `defaultValue` dos
 * campos dele e as regras de validação que a submissão final precisa checar.
 *
 * Avançar valida só os `campos` do passo atual. Se mesmo assim a submissão
 * encontrar erro (um campo que o passo não listou), a jornada volta para o
 * passo onde o erro está em vez de falhar sem mostrar nada.
 *
 * `livre` é o modo de EDIÇÃO: a ficha já existe, e quem abre quer mudar um
 * campo, não percorrer a jornada. Os passos viram atalhos clicáveis e o botão
 * de gravar aparece em todos eles.
 *
 * A área do passo tem ALTURA FIXA (que acompanha a altura da tela) e rola por
 * dentro: com a altura seguindo o conteúdo, o diálogo encolhia e esticava a
 * cada Avançar, e o botão mudava de lugar debaixo do cursor.
 */
export interface PassoDoFormulario {
  id: string;
  rotulo: string;
  titulo: ReactNode;
  apoio?: string;
  /** Campos validados ao avançar. Passo só de campos opcionais não precisa. */
  campos?: string[];
  conteudo: ReactNode;
}

export const JornadaDoFormulario = ({
  passos,
  acaoFinal,
  acaoSecundaria,
  livre = false,
}: {
  passos: PassoDoFormulario[];
  /** O botão de gravar (tipo submit), que só aparece no último passo. */
  acaoFinal: ReactNode;
  /** Ao lado do Avançar em todos os passos — ex.: Cancelar. */
  acaoSecundaria?: ReactNode;
  /** Edição: passos clicáveis e `acaoFinal` em todos eles. */
  livre?: boolean;
}) => {
  const {
    trigger,
    formState: { errors, submitCount },
  } = useFormContext();
  const [indice, setIndice] = useState(0);
  const topo = useRef<HTMLDivElement>(null);
  const areaDoPasso = useRef<HTMLDivElement>(null);
  const ultimo = indice === passos.length - 1;
  const passo = passos[indice];

  const irPara = (i: number) => {
    setIndice(Math.max(0, Math.min(i, passos.length - 1)));
    // Dentro de um diálogo com rolagem, o passo novo começa lá do alto.
    topo.current?.scrollIntoView?.({ block: "nearest" });
    if (areaDoPasso.current) areaDoPasso.current.scrollTop = 0;
  };

  useEffect(() => {
    if (!submitCount) return;
    const comErro = passos.findIndex((p) => p.campos?.some((c) => get(errors, c)));
    if (comErro >= 0) irPara(comErro);
    // Só reage a uma submissão nova; `errors` muda a cada tecla depois disso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const avancar = async () => {
    const campos = passo.campos ?? [];
    if (campos.length && !(await trigger(campos, { shouldFocus: true }))) return;
    irPara(indice + 1);
  };

  /**
   * Enter num campo avança em vez de não fazer nada. Fica de fora quando outro
   * componente já tratou a tecla (a lista de um autocomplete escolhendo uma
   * opção) e em textarea, onde Enter é quebra de linha.
   */
  const aoTeclar = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" || ultimo || e.defaultPrevented) return;
    const alvo = e.target as HTMLElement;
    if (alvo.tagName !== "INPUT" || alvo.getAttribute("role") === "combobox") return;
    e.preventDefault();
    void avancar();
  };

  return (
    <div ref={topo} className="flex flex-col gap-6" onKeyDown={aoTeclar}>
      <PassosBento passos={passos} indice={indice} aoEscolher={livre ? irPara : undefined} />

      <div className="flex h-[clamp(260px,calc(90dvh-17rem),520px)] flex-col gap-6">
        <div>
          <h2 className="text-[17px] font-bold leading-tight tracking-[-0.02em]">{passo.titulo}</h2>
          {passo.apoio && (
            <p className="mt-1 text-[13px] text-[#6b7480] dark:text-muted-foreground">{passo.apoio}</p>
          )}
        </div>

        {/* px/-mx: o anel de foco dos campos não é cortado pela rolagem. */}
        <div ref={areaDoPasso} className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-1">
          {passos.map((p, i) => (
            <section key={p.id} hidden={i !== indice} aria-label={p.rotulo}>
              <div className="flex flex-col gap-4">{p.conteudo}</div>
            </section>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[rgba(53,70,96,0.09)] pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => irPara(indice - 1)}
          disabled={indice === 0}
          className="text-[#6b7480] hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div className="flex items-center gap-2">
          {acaoSecundaria}
          {livre && !ultimo && (
            <Button type="button" variant="outline" onClick={() => void avancar()}>
              Avançar
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
          {ultimo || livre ? (
            acaoFinal
          ) : (
            <Button type="button" onClick={() => void avancar()} className="min-w-[140px]">
              Avançar
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
